import React, { useState, useEffect, use } from 'react';
import ReviewStatusCards from '../cards/ReviewStatusCards';
import {VideoFilters, MobileVideoFilter, FilterIcon} from './VideoFilters';
// do this import PinnedVideosSection from './PinnedVideosSection';
import VideoGrid from './VideoGrid';
import VideoEditModal from './VideoEditModal';
import GalleryHeader from './GalleryHeader';
import StatusBadge from '../ui/StatusBadge';
import { fetchVideoById, type UserVideo } from '@/api/videoApi';
import { useScrollToTop } from '@/hooks/common/ScrollToTop';
import { useInfiniteScroll } from '@/hooks/common/customHooks';
import { ScrollToTopButton } from '@/components/ui/ScrollToTopButton';
import { type UserImage } from '@/api/imageApi';
// do import { createPinHandler, createUnpinHandler } from '@/utils/optimistic/optimisticPin';
import { 
  ImageIcon, 
  WarningIcon, 
  SearchIcon, 
  CheckIcon, 
  StateDisplay 
} from '@/components/ui/common/GallerySharedComponents';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import Show from '@/utils/common/Show';
import ToggleTab, { useToggleTabState } from '@/components/dashboard/ui/ToggleTab';
import { Match, Switch } from '@/utils/common/Switch';
import { TogglePage } from '@/components/dashboard/ui/TogglePage';
import VirtualizedImageGrid from '@/components/virtualized/VirtualizedImageGrid';
import { ImageRowSkeleton } from '@/components/ui/skeleton/Skeleton';
import { useResponsiveColumns, IMAGE_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';
import VideoExtensionRequestsPanel from '../cards/VideoExtensionRequestsPanel';
import { useExtensionRequestUsers } from '@/hooks/querry_hooks';

// End of content indicator component
const EndOfContentIndicator: React.FC<{ message: string; subMessage?: string }> = ({ message, subMessage }) => (
  <div className="flex justify-center items-center py-8">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
        <CheckIcon className="text-green-600" />
      </div>
      <p className="text-gray-600 font-medium text-sm">{message}</p>
      {subMessage && <p className="text-gray-500 text-xs mt-1">{subMessage}</p>}
    </div>
  </div>
);

interface User {
  email: string
  count: number
  role: string
}

interface AdminVideoGalleryProps {
  isAdmin: boolean;
  userEmail: string | null;
  videos: UserVideo[];
  pinnedVideos: UserVideo[];
  error?: string | null;
  onRetry?: () => void;
  onDeleteVideo?: (videoId: string) => Promise<void>;
  isLoading?: boolean;
  // isLoadingPinnedVideos?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  allUsers?: User[];
  isLoadingUsers?: boolean;
  statusCounts?: {
    approved: number;
    pending: number;
    rejected: number;
    total: number;
    approvedDuration: number;
    pendingDuration: number;
    rejectedDuration: number;
    totalDuration: number;
  };
  totalCount: number;
  handleDownloadLastFrame?: (signedUrl: string) => Promise<void>;
  // optimisticPinVideo?: (videoId: string, userEmail: string) => void;
  // optimisticUnpinVideo?: (videoId: string) => void;
  // pinVideoWebSocket?: (videoId: string, user_Email?: string) => void;
  // unpinVideoWebSocket?: (videoId: string, user_Email?: string) => void;
  serverFilters: {
    status?: string;
    user_email?: string;
    sort_by?: string;
  };
  setServerFilters: React.Dispatch<React.SetStateAction<{ status?: string; user_email?: string; sort_by?: string }>>;
  loadMore: () => void;
  pageValue?: string;
  setPageValue?: React.Dispatch<React.SetStateAction<'Videos' | 'Images' | 'ExtensionRequests'>>;
  // Image-related props
  images?: UserImage[];
  imagesError?: string | null;
  onRetryImages?: () => void;
  onDeleteImage?: (imageId: string) => Promise<void>;
  isLoadingImages?: boolean;
  imagesTotalCount?: number;
  // Toast callbacks
  onFavouriteSuccess?: (isFavourited: boolean, type: 'video' | 'image') => void;
  onArchiveSuccess?: (isArchived: boolean, type: 'video' | 'image') => void;
}


type SortOption = 'recent' | 'oldest' | 'largest' | 'smallest' | 'user_asc' | 'user_desc';

const AdminVideoGallery: React.FC<AdminVideoGalleryProps> = ({
  isAdmin,
  userEmail,
  videos,
  pinnedVideos,
  error = null,
  onRetry,
  onDeleteVideo,
  isLoading = false,
  // isLoadingPinnedVideos = false,
  isLoadingMore = false,
  hasMore = false,
  allUsers = [],
  isLoadingUsers = false,
  statusCounts,
  totalCount,
  handleDownloadLastFrame,
  // optimisticPinVideo,
  // optimisticUnpinVideo,
  // pinVideoWebSocket,
  // unpinVideoWebSocket,
  serverFilters,
  setServerFilters,
  loadMore,
  pageValue,
  setPageValue,
  // Image props
  images = [],
  imagesError = null,
  onRetryImages,
  onDeleteImage,
  isLoadingImages = false,
  imagesTotalCount = 0,
  // Toast callbacks
  onFavouriteSuccess,
  onArchiveSuccess,
}) => {
  const [selectedVideo, setSelectedVideo] = useState<UserVideo | null>(null);
  const [selectedImage, setSelectedImage] = useState<UserImage | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const { showButton, scrollToTop } = useScrollToTop();

  const { data: extReqUsers = [], isLoading: isLoadingExtReqUsers } = useExtensionRequestUsers(isAdmin);
  const effectiveUsers = pageValue === 'ExtensionRequests' ? extReqUsers : allUsers;
  const effectiveLoadingUsers = pageValue === 'ExtensionRequests' ? isLoadingExtReqUsers : isLoadingUsers;

  // Listen for notification video modal events
  useEffect(() => {
    const handleOpenVideoModal = async (event: CustomEvent) => {
      const { videoId } = event.detail;
      
      // First try to find the video in the current videos
      let video = videos.find(v => v.id === videoId) || 
                  pinnedVideos.find(v => v.id === videoId);
      
      if (video) {
        setSelectedVideo(video);
      } else {
        // If video not found in current list, try to fetch it specifically
        try {
          if (!userEmail) return;

          const video = await fetchVideoById(videoId, true); // true for admin endpoint
          
          if (video) {
            setSelectedVideo(video);
          } else {
            // Video not found or deleted
            alert('Video not found. It may have been deleted.');
          }
        } catch (error) {
          console.error('Error fetching video:', error);
          alert('Error loading video details.');
        }
      }
    };

    globalThis.addEventListener('openAdminVideoModal', handleOpenVideoModal as any);
    return () => {
      globalThis.removeEventListener('openAdminVideoModal', handleOpenVideoModal as any);
    };
  }, [videos, pinnedVideos]);



  const handleStatusCardClick = (status: string) => {
    const newStatus = status === serverFilters.status ? 'all' : status;
    setServerFilters({...serverFilters, status: newStatus});
  };

  // Generic filter handler function
  const handleFilterChange = (key: 'sort_by' | 'user_email' | 'status', value: string) => {
    // Use functional setState to avoid stale state when called multiple times in succession
    setServerFilters(prev => ({...prev, [key]: value}));
  };

  useInfiniteScroll({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore || isLoading,
    onLoadMore: loadMore,
    threshold: 200,
  })

  // Filter and sort videos
  const filteredVideos = videos;

  // Pin/Unpin handlers - ONLY ALLOW ADMINS to pin/unpin on content review page
  // Content creators cannot pin/unpin on content review page
  // const handlePinVideo = (isAdmin && optimisticPinVideo && pinVideoWebSocket)
  //   ? createPinHandler(optimisticPinVideo, pinVideoWebSocket, userEmail || '', 'admin')
  //   : undefined;

// Pin/Unpin handlers - ONLY ALLOW ADMINS to pin/unpin on content review page
  // const handleUnpinVideo = (isAdmin && optimisticUnpinVideo && unpinVideoWebSocket)
  //   ? createUnpinHandler(optimisticUnpinVideo, unpinVideoWebSocket, 'admin')
  //   : undefined;

  // Modal handlers - consolidated close function
  const openVideoModal = (video: UserVideo) => {
    setSelectedVideo(video);
  };

  const openImageModal = (image: UserImage) => {
    setSelectedImage(image);
  };

  const closeAllModals = () => {
    setSelectedVideo(null);
    setSelectedImage(null);
  };

  // Create status badge function
  const getStatusBadge = (status?: string) => <StatusBadge status={status} />;

  // Determine content type for modal
  const getContentType = () => {
    if (selectedVideo) return 'video';
    if (selectedImage) return 'image';
    return undefined;
  };

  // Get responsive columns for images
  const imageColumns = useResponsiveColumns(IMAGE_GRID_COLUMNS);

  // Render images grid content based on loading/error states
  const renderImagesContent = () => {
    // Show skeleton loading state for images
    if (isLoadingImages && !images.length) {
      return (
        <div className="space-y-4">
          {['admin-img-skeleton-0', 'admin-img-skeleton-1', 'admin-img-skeleton-2', 'admin-img-skeleton-3'].map((key, i) => (
            <ImageRowSkeleton key={key} columns={imageColumns} rowIndex={i} />
          ))}
        </div>
      );
    }

    // Show error state for images
    if (imagesError && !isLoadingImages) {
      return (
        <StateDisplay
          icon={<WarningIcon className="w-8 h-8 text-red-600" />}
          iconBgColor="bg-red-100"
          title="Error Loading Images"
          description={imagesError}
          actionButton={
            onRetryImages && (
              <button
                onClick={onRetryImages}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Try Again
              </button>
            )
          }
        />
      );
    }
    
    // Show "No images found" when user has images but filters result in no matches
    if (imagesTotalCount > 0 && !images.length && !isLoadingImages) {
      return (
        <StateDisplay
          icon={<SearchIcon className="w-8 h-8 text-gray-600" />}
          iconBgColor="bg-gray-100"
          title="No Images Found"
          description="Try adjusting your filters to see more results"
        />
      );
    }
    
    // Show "No images yet" only when user has no images at all
    if (!imagesTotalCount && !isLoadingImages) {
      return (
        <StateDisplay
          icon={<ImageIcon className="w-8 h-8 text-blue-600" />}
          iconBgColor="bg-blue-100"
          title="No Images Yet"
          description="No images have been generated yet. They will appear here once uploaded!"
        />
      );
    }

    return (
      <>
        {/* Virtualized Image Grid */}
        <VirtualizedImageGrid
          images={images}
          isLoading={isLoadingImages}
          isLoadingMore={isLoadingMore && pageValue === 'Images'}
          onImageClick={openImageModal}
          showUserEmail={true}
          isAdmin={isAdmin}
          onFavouriteSuccess={(isFavourited: boolean) => onFavouriteSuccess?.(isFavourited, 'image')}
          onArchiveSuccess={(isArchived: boolean) => onArchiveSuccess?.(isArchived, 'image')}
        />

        {/* End of Content Indicator for Images */}
        {!hasMore && images.length > 0 && !isLoadingMore && pageValue === 'Images' && (
          <EndOfContentIndicator 
            message="You've reached the end!" 
            subMessage="All images have been loaded"
          />
        )}
      </>
    );
  };

  // Render main content based on page value
  const renderMainContent = () => {
    if (!pageValue || pageValue === 'Videos') {
      return (
        <VideoGrid
          videos={filteredVideos}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          error={error}
          hasMore={hasMore}
          totalCount={totalCount}
          onVideoClick={openVideoModal}
          // do onPinVideo={handlePinVideo}
          // onUnpinVideo={handleUnpinVideo}
          onRetry={onRetry}
          getStatusBadge={getStatusBadge}
          // isAdmin={isAdmin}
          onFavouriteSuccess={(isFavourited: boolean) => onFavouriteSuccess?.(isFavourited, 'video')}
          // onArchiveSuccess={(isArchived: boolean) => onArchiveSuccess?.(isArchived, 'video')}
        />
      );
    }

    if (pageValue === 'Images') {
      return (
        <div className={`flex flex-col space-y-6 ${isMobile ? 'px-0' : 'px-6'}`}>
          {renderImagesContent()}
        </div>
      );
    }

    if (pageValue === 'ExtensionRequests') {
      return (
        <div className={`flex flex-col space-y-6 ${isMobile ? 'px-0' : 'px-6'}`}>
          {renderExtensionRequests()}
        </div>
      );
    }

    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Transcripts Coming Soon</h3>
        <p className="text-gray-600">
          Transcript review and management will be available in the next update.
        </p>
      </div>
    );
  };

  // Alias used in JSX below
  const renderExtensionRequests = () => (
    <VideoExtensionRequestsPanel
      filterUser={serverFilters.user_email || 'all'}
      filterStatus={serverFilters.status || 'all'}
      onViewExtended={async (videoId: string) => {
        try {
          const video = await fetchVideoById(videoId, true);
          if (video) setSelectedVideo(video);
        } catch {
          // ignore
        }
      }}
    />
  );

  // Only show "All" tab, pinned section is commented out
  const tabOptions = [
    { value: "All", label: "All" },
  ];

  const { isMobile } = use(DeviceContext) ?? {isMobile: false};
  const { activeTab, handleTabChange } = useToggleTabState({
    options: tabOptions,
    defaultValue: "All",
    storageKey: "video-gallery-tab",
  });


  return (
    <div className="mt-12 sm:mt-16">
      {/* Status Cards */}
      {statusCounts && pageValue !== 'ExtensionRequests' && (
        <ReviewStatusCards
          approvedCount={statusCounts.approved}
          pendingCount={statusCounts.pending}
          rejectedCount={statusCounts.rejected}
          approvedDuration={statusCounts.approvedDuration || 0}
          pendingDuration={statusCounts.pendingDuration || 0}
          rejectedDuration={statusCounts.rejectedDuration || 0}
          onStatusClick={handleStatusCardClick}
          activeStatus={serverFilters.status || 'all'}
          showDuration={pageValue !== 'Images'} // Hide duration for images
        />
      )}

      {/* Fallback title if statusCounts are not available */}
      {!statusCounts && pageValue !== 'ExtensionRequests' && (
        <GalleryHeader 
          title="All User Generated Content"
          showDecorativeLine={true}
        />
      )}

      {/* Show toggle tab for mobile - only "All" tab (Pinned section commented out) */}
      <Show when={pageValue === 'Videos' && isMobile}>
        <div className="flex items-center justify-center mb-8">
          <ToggleTab
            value={activeTab}
            onChange={handleTabChange}
            options={tabOptions}
          />
        </div>
      </Show>

      {/* Commented out Pinned Videos Section */}
      {/* <Show when={pageValue === 'Videos' && (!isMobile || (isMobile && activeTab === 'Pinned'))}>
        <PinnedVideosSection
          isAdmin={isAdmin}
          pinnedVideos={pinnedVideos}
          isLoadingPinnedVideos={isLoadingPinnedVideos}
          selectedAdmin={selectedAdmin}
          onAdminChange={setSelectedAdmin}
          onVideoClick={openVideoModal}
          onPinVideo={handlePinVideo}
          onUnpinVideo={handleUnpinVideo}
          getStatusBadge={getStatusBadge}
          error={error}
          isMobile={isMobile}
          onFavouriteSuccess={(isFavourited: boolean) => onFavouriteSuccess?.(isFavourited, 'video')}
          onArchiveSuccess={(isArchived: boolean) => onArchiveSuccess?.(isArchived, 'video')}
        />
      </Show> */}
      
      {/* Filter Controls */}
      <Switch>
        <Match when={!isMobile}>
          <VideoFilters
            isAdmin={isAdmin}
            sortBy={serverFilters.sort_by as SortOption || 'recent'}
            onSortChange={(sort) => handleFilterChange('sort_by', sort)}
            selectedUser={serverFilters.user_email || 'all'}
            onUserChange={(user) => handleFilterChange('user_email', user)}
            selectedStatus={serverFilters.status || 'all'}
            onStatusChange={(status) => handleFilterChange('status', status)}
            allUsers={effectiveUsers}
            isLoadingUsers={effectiveLoadingUsers}
            pageValue={pageValue}
            setPageValue={setPageValue}
            showUserFilter={true}
            hideSortBy={pageValue === 'ExtensionRequests'}
          />
        </Match>
        <Match when={isMobile}>
          <div className='flex items-center justify-between mx-4 mb-8'>
            <FilterIcon onClick={() => setIsFilterOpen(true)} />
            <MobileVideoFilter
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              isAdmin={isAdmin}
              sortBy={serverFilters.sort_by as SortOption || 'recent'}
              onSortChange={(sort) => handleFilterChange('sort_by', sort)}
              selectedUser={serverFilters.user_email || 'all'}
              onUserChange={(user) => handleFilterChange('user_email', user)}
              selectedStatus={serverFilters.status || 'all'}
              onStatusChange={(status) => handleFilterChange('status', status)}
              allUsers={effectiveUsers}
              isLoadingUsers={effectiveLoadingUsers}
            />
            <TogglePage 
              value={pageValue || 'Videos'} 
              handleChange={(_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
                if (newValue && setPageValue) {
                  setPageValue(newValue as 'Videos' | 'Images' | 'ExtensionRequests');
                }
              }}
              showTranscripts={false}
              showExtensionRequests={isAdmin}
            />
          </div>
        </Match>
      </Switch>

      {/* Content Grid - Videos or Images based on pageValue */}
      <Show when={!isMobile || (isMobile && activeTab === 'All')}>
        {renderMainContent()}
      </Show>
      

      {/* Content Edit Modal */}
      <VideoEditModal
        isAdmin={isAdmin}
        video={selectedVideo}
        image={selectedImage}
        isOpen={!!(selectedVideo || selectedImage)}
        onClose={closeAllModals}
        onDeleteVideo={onDeleteVideo}
        onDeleteImage={onDeleteImage}
        handleDownloadLastFrame={handleDownloadLastFrame}
        contentType={getContentType()}
        adminEmail={userEmail || undefined}
      />
        {showButton && (
          <ScrollToTopButton scrollToTop={scrollToTop} />
        )}
    </div>
  );
};

export default AdminVideoGallery;
